import { ServicePageShowImage_productizedService$key } from '#app/__generated__/relay/ServicePageShowImage_productizedService.graphql.js';
import { ServicePageShowImageUpdateProductizedServiceMutation } from '#app/__generated__/relay/ServicePageShowImageUpdateProductizedServiceMutation.graphql.js';
import { ProjectCoverImageWithUpload } from '#app/components/ProjectCoverImageWithUpload/ProjectCoverImageWithUpload.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import {
  ContraMutationErrors,
  useContraMutation,
} from '#app/hooks/useContraMutation.js';
import { useMatchMedia } from '#app/hooks/useMatchMedia.js';
import { UploadPayload, useUpload } from '#app/hooks/useUpload.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { readFile } from '#app/services/file.js';
import { logger } from '#app/services/logger.js';
import { parseMediaQueryForMatchMedia } from '#app/utilities/parseMediaQueryForMatchMedia.js';
import { graphql, useFragment } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';

const log = logger.child({
  namespace: 'ServiceCoverImage',
});

const DEFAULT_ERROR_MESSAGE =
  'Oops! Something went wrong updating your service image. Our team has been notified. Please try again.';

const DEFAULT_SUCCESS_MESSAGE =
  'You service cover image was successfully updated!';

export const ServicePageShowImage = ({
  isReadOnly = false,
  productizedServiceRef,
  width,
}: {
  readonly isReadOnly?: boolean;
  readonly productizedServiceRef: ServicePageShowImage_productizedService$key;
  readonly width?: number;
}) => {
  const { track } = useAnalytics();
  const [urlSearchParameters] = useURLParameters('/s/@slug');
  const toast = useToast();

  const service = useFragment<ServicePageShowImage_productizedService$key>(
    graphql`
      fragment ServicePageShowImage_productizedService on ProductizedService {
        productizedServiceId: id
        visitorCanEdit
        title
        publishedAt
        cover {
          ...ProjectCoverWrapper_portfolioProjectCover
        }
      }
    `,
    productizedServiceRef,
  );

  const { productizedServiceId, publishedAt, title, visitorCanEdit } = service;

  const [upload, isUploading] = useUpload('image');

  const [updateService, isUpdatingService] =
    useContraMutation<ServicePageShowImageUpdateProductizedServiceMutation>(
      graphql`
        mutation ServicePageShowImageUpdateProductizedServiceMutation(
          $input: UpdateProductizedServiceInput!
        ) {
          updateProductizedService(input: $input) {
            errors {
              __typename
              message
            }
            productizedService {
              id
              ...ServicePageShowImage_productizedService
            }
          }
        }
      `,
    );

  const handleUpdateServiceImageError = useCallback(
    (errorMessage = DEFAULT_ERROR_MESSAGE) => {
      toast.error(errorMessage);
      log.error(errorMessage);
    },
    [toast],
  );

  const handleUpdateServiceImageSuccess = useCallback(() => {
    toast.success(DEFAULT_SUCCESS_MESSAGE);
    log.info(DEFAULT_SUCCESS_MESSAGE);

    track('upload_asset', {
      asset_name: 'service_cover',
      asset_type: 'image',
      upload_status: 'complete',
    });
  }, [toast, track]);

  const handleMutationErrors = useCallback(
    (mutationErrors: ContraMutationErrors) => {
      const { apiErrors, hasErrors, payloadErrors } = mutationErrors;
      if (hasErrors) {
        log.error('service update mutation failed');

        if (payloadErrors) {
          for (const { message } of payloadErrors) {
            handleUpdateServiceImageError(message);
            captureException(message);
          }
        }

        if (apiErrors) {
          toast.error(DEFAULT_ERROR_MESSAGE);
          for (const { message } of apiErrors) {
            captureException(message);
          }
        }
      }
    },
    [handleUpdateServiceImageError, toast],
  );

  const handleUploadComplete = useCallback(
    (payload: UploadPayload) => {
      log.info(
        { payload },
        'image was uploaded, updating service with new image',
      );

      updateService({
        onCompleted: (mutationErrors, response) => {
          if (mutationErrors.hasErrors) {
            handleMutationErrors(mutationErrors);
            return;
          }

          if (response.updateProductizedService.productizedService?.id) {
            handleUpdateServiceImageSuccess();
          }
        },
        onError: (error) => {
          handleUpdateServiceImageError();
          captureException(error);
        },
        variables: {
          input: {
            coverImage: {
              animated: payload.animated,
              checksum: payload.checksum,
              height: payload.height,
              originalFormat: payload.originalFormat,
              sizeBytes: payload.sizeBytes,
              uid: payload.uid,
              width: payload.width,
            },
            id: productizedServiceId,
            // if the service was already published, then renew the date,
            // otherwise leave it in draft state by keeping the date null
            publishedAt,
          },
        },
      });
    },
    [
      handleMutationErrors,
      handleUpdateServiceImageError,
      handleUpdateServiceImageSuccess,
      productizedServiceId,
      publishedAt,
      updateService,
    ],
  );

  const handleImageUploadErrors = useCallback(
    (uploadErrors: string[]) => {
      for (const errorMessage of uploadErrors) {
        handleUpdateServiceImageError(errorMessage);
      }
    },
    [handleUpdateServiceImageError],
  );

  const handleImageUpload = useCallback(
    async (file: File) => {
      try {
        log.info('attempting to upload new image');
        const dataUrl = await readFile(file);

        track('upload_asset', {
          asset_name: 'service_cover',
          asset_type: 'image',
          file_size: file.size,
          file_type: file.type,
          upload_status: 'start',
        });

        upload({
          file: dataUrl,
          onCompleted: handleUploadComplete,
          onError: (error) => {
            track('upload_asset', {
              asset_name: 'service_cover',
              asset_type: 'image',
              file_size: file.size,
              file_type: file.type,
              upload_status: 'error',
            });
            throw error;
          },
          preset: 'projectCover',
        });
      } catch (error) {
        handleUpdateServiceImageError();
        captureException(error);
        log.info({ error }, 'there was an error uploading the image');
      }
    },
    [handleUpdateServiceImageError, handleUploadComplete, track, upload],
  );

  const handleOpenFileSelectDialog = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'demand_service_cover_image_file_select',
      section: 'service_cover_image',
    });
  }, [track]);

  const theme = useTheme();
  const isMediumScreen = useMatchMedia(
    parseMediaQueryForMatchMedia(theme.mediaQueries.sm),
  );

  const isExtraLargeScreen = useMatchMedia(
    parseMediaQueryForMatchMedia(theme.mediaQueries.xl),
  );

  const targetWidth = useMemo(() => {
    if (width) {
      return width;
    }

    if (isExtraLargeScreen) {
      return 343;
    }

    if (isMediumScreen) {
      return 1_000;
    }

    return 500;
  }, [isExtraLargeScreen, isMediumScreen, width]);

  const isResolvingUploadAndServiceUpdate = isUploading || isUpdatingService;
  const isPreview =
    service && !service.publishedAt && urlSearchParameters.preview;

  return (
    <ProjectCoverImageWithUpload
      ariaLabel={`Cover image for ${title}`}
      canUpload={!isPreview && !isReadOnly && visitorCanEdit}
      coverRef={service.cover}
      handleImageUpload={handleImageUpload}
      handleImageUploadErrors={handleImageUploadErrors}
      headline="Add a service cover image"
      includeNewBadge
      isLoading={isResolvingUploadAndServiceUpdate}
      onFileDialogOpen={handleOpenFileSelectDialog}
      subHeadline="Capture Clients’ attention with visual examples of your services."
      width={targetWidth}
    />
  );
};
